import React, { useEffect, useState, useMemo } from 'react';
import { Link, useHistory } from "react-router-dom";
import _ from 'lodash';
import { OverlayTrigger } from 'react-bootstrap';
import { Card, Button, Modal, Popover, ProgressBar } from 'react-bootstrap';
import {EvaluationMark} from './EvaluationMark';
import { EvaluationBar } from './EvaluationBar';
import Evaluation from '../../models/Evaluation';

import MultipointEvaluation from '../../models/MultipointEvaluation';
import { FaExclamationTriangle, FaMapMarkerAlt, FaEdit, FaAsterisk, FaCircle, FaRegCircle, FaStar, FaRegStar } from 'react-icons/fa';
import { FcBusinesswoman } from 'react-icons/fc';
import { MdPlaylistAddCheck, MdEdit, MdCheck, MdClear } from 'react-icons/md';

import moment from 'moment';
import {useFilterContext} from '../../context/SharedFilterContext';
import { GOOGLEMAP_API_KEY } from '../../constants/Constants';
import {processContext} from './ReportTable';
import ImageUploading from 'react-images-uploading';
import Property from '../../models/Property';
import User from '../../models/User';
import Check from '../../models/Check';
import Utils from "../../common/Utils";
import { ImSpinner3 } from 'react-icons/im';
import { useAuth } from '../../context/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import Val from '../../models/Val';
import pLimit from 'p-limit';
import PropertyHealthScoreModal from './PropertyHealthScoreModal';


const PropertyCard = (props) => {
  const { 
    index, 
    row, 
    hideAnalysisColors, 
    hideStructureInfo, 
    hideInspectionInfo,
    setSelectedPropertyResult, 
    setMapCoordinates, 
    hideMapLocation,
    hideReportInfo,
    mapLat, 
    mapLng, 
    hasExternalUuid, 
    sortByReports,
    onFavoritesUpdate,
    isInFavoritedView = false, // NEW: Prop to indicate favorited view
    currentUserData: parentCurrentUserData, // Passed from grid to avoid redundant User.findByUUID calls
  } = props;

  const { user, isSuperAdmin } = useAuth();

  const { startDate, endDate} = useFilterContext();
  const [property, setProperty] = useState({});
  const [pointInfos, setPointInfos] = useState([]);
  const [evaluationsCount, setEvaluationsCount] = useState(0);
  const [latestUserId, setLatestUserId] = useState(null);
  const [latestUserName, setLatestUserName] = useState('');
  const [mostRecentDate, setMostRecentDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingChecks, setLoadingChecks] = useState(false);
  const [cacheBust, setCacheBust] = useState(null);
  const [showMPeButton, setShowMPeButton] = useState(false); 
  const [showMPeAlreadyRunButton, setShowMPeAlreadyRunButton] = useState(false); // State to show/hide non-clickable MPe button
  const [mostRecentEvaluationDate, setMostRecentEvaluationDate] = useState(null);

  const [progress, setProgress] = useState(0); // State to track progress
  const [totalSteps, setTotalSteps] = useState(0); // State to track total steps
  const [showProgress, setShowProgress] = useState(false); // State to show/hide progress bar

  const [isFavorited, setIsFavorited] = useState(false);
  const [updatingFavorite, setUpdatingFavorite] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);

  // NEW: Add state for health score data and loading state
  const [healthScoreData, setHealthScoreData] = useState(null);
  const [loadingHealthScore, setLoadingHealthScore] = useState(false);

  // Add new state for property health score modal
  const [showPropertyHealthModal, setShowPropertyHealthModal] = useState(false);
  const [allPropertyHealthScores, setAllPropertyHealthScores] = useState([]);
  const [hasPropertyHealthScores, setHasPropertyHealthScores] = useState(false);

  // Convert local dates to UTC moments for consistent filtering
  const startMoment = useMemo(() => moment(startDate).startOf('day').utc(), [startDate]);
  const endMoment = useMemo(() => moment(endDate).endOf('day').utc(), [endDate]);

  const filteredEdges = useMemo(() => {
    return (property.vals?.edges || []).filter(edge => {
      // Parse the UTC timestamp from the server
      const recordedAtMoment = moment.utc(edge.node.recorded_at);
      return recordedAtMoment.isBetween(startMoment, endMoment, null, '[]');
    });
  }, [property.vals?.edges, startMoment, endMoment]);

  const uniquePointNumSet = useMemo(() => {
    return new Set(filteredEdges.map(edge => edge.node.point_num));
  }, [filteredEdges]);

  const uniquePointNumCount = Object.keys(_.groupBy(filteredEdges, x => x.node.point_num)).length;

  useEffect(() => {
    const uniquePointNums = [...new Set(property?.pointInfos?.edges?.map(pi => pi.node.num))];
    setPointInfos(uniquePointNums);
  }, [property, uniquePointNumSet]);

  const extractPointName = row => row.point_info ? row.point_info.measurement_name : "";

  const calculatePerpetualScore = (points) => {
    const multipliers = { E1: 4, E2: 3, E3: 2, E4: 1, E5: 0 };
    let numerator = 0;
    let denominator = points.length * 4;
    points.forEach(point => {
      if (point.evalNum !== null) {
        let category;
        if (point.evalNum >= 1 && point.evalNum <= 5) category = 'E1';
        else if (point.evalNum >= 6 && point.evalNum <= 10) category = 'E2';
        else if (point.evalNum >= 11 && point.evalNum <= 15) category = 'E3';
        else if (point.evalNum >= 16 && point.evalNum <= 20) category = 'E4';
        else if (point.evalNum >= 21 && point.evalNum <= 25) category = 'E5';
        if (category) {
          numerator += multipliers[category];
        }
      }
    });
    return (numerator / denominator) * 100;
  };

  // Fixed filterVals function to handle UTC timestamps properly
  const filterVals = (vals) => {
    return (vals || []).filter(val => {
      // Parse the UTC timestamp from the server
      const recordedAtMoment = moment.utc(val?.recorded_at);
      return recordedAtMoment.isBetween(startMoment, endMoment, null, '[]');
    });
  }

  let allVals = filterVals(property?.vals?.edges?.map(x => x.node)) || []
  const allPointInfos = property?.pointInfos?.edges?.map(x => x.node) || [];
 
  const allMatchedPointInfos = allPointInfos.map(pointInfo => {
  const matchingVal = allVals.find(val => val.point_num === pointInfo.num);
  if (matchingVal) {
    return {
      ...pointInfo,
      matchedValUuid: matchingVal.uuid,
    };
  }
  return pointInfo;
}).filter(pointInfo => pointInfo.matchedValUuid);
  const groupedPointInfos = _.groupBy(allMatchedPointInfos, 'fig_id');
  const figValUuids = Object.keys(groupedPointInfos).map(fig_id => ({
    fig: groupedPointInfos[fig_id][0].fig,
    valUuids: groupedPointInfos[fig_id].map(info => info.matchedValUuid)
  }));


  const generateHash = async (inspections) => {
    const selectAndSortFields = (obj) => {
      if (Array.isArray(obj)) {
        return obj.map(selectAndSortFields);
      } else if (obj !== null && typeof obj === 'object') {
        const relevantFields = ['uuid', 'updated_at']; // List relevant fields
        const sortedObj = relevantFields.reduce((sorted, key) => {
          if (obj[key] !== undefined) {
            sorted[key] = selectAndSortFields(obj[key]);
          }
          return sorted;
        }, {});
        return sortedObj;
      }
      return obj;
    };
  
    const filteredInspections = inspections.map(selectAndSortFields);
  
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(filteredInspections));
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex; 
  };
  
  


  const red = useMemo(() => {
    const value = allMatchedPointInfos?.filter(pi => pi?.evalNum && pi?.evalNum > 20)?.length;
    return value;
  }, [allMatchedPointInfos, property.name]);

  const amber = useMemo(() => {
    const value = allMatchedPointInfos?.filter(pi => pi?.evalNum && pi?.evalNum > 15 && pi?.evalNum <= 20)?.length;
    return value;
  }, [allMatchedPointInfos, property.name]);

  const yellow = useMemo(() => {
    const value = allMatchedPointInfos?.filter(pi => pi?.evalNum && pi?.evalNum > 10 && pi?.evalNum <= 15)?.length;
    return value;
  }, [allMatchedPointInfos, property.name]);

  const lime = useMemo(() => {
    const value = allMatchedPointInfos?.filter(pi => pi?.evalNum && pi?.evalNum > 5 && pi?.evalNum <= 10)?.length;
    return value;
  }, [allMatchedPointInfos, property.name]);

  const green = useMemo(() => {
    const value = allMatchedPointInfos?.filter(pi => pi?.evalNum && pi?.evalNum >= 0 && pi?.evalNum <= 5)?.length;
    return value;
  }, [allMatchedPointInfos, property.name]);

  const total = allPointInfos.length;

  const gray = useMemo(() => {
    const value = total - red - amber - yellow - lime - green;
    return value;
  }, [total, red, amber, yellow, lime, green, property.name]);

  // NEW: Determine if we should fetch health score data
  const shouldFetchHealthScore = useMemo(() => {
    return !hideInspectionInfo;
  }, [hideInspectionInfo]);

  // NEW: Get health score from fetched data or calculate from pointInfos
  const dstHealthScore = useMemo(() => {
    if (shouldFetchHealthScore && healthScoreData && healthScoreData.score !== undefined) {
      return healthScoreData.score;
    }
    // Fallback to calculated score only when not fetching health score data
    return shouldFetchHealthScore ? null : calculatePerpetualScore(allMatchedPointInfos);
  }, [shouldFetchHealthScore, healthScoreData, allMatchedPointInfos]);

  const pointsTotal = useMemo(
    () => property?.pointInfos?.totalCount ?? 0,
    [property?.pointInfos?.totalCount]
  );


  useEffect(() => {
    const abortController = new AbortController();
    setProperty(row);
    const fetchPropertyFigs = async () => {
      try {
        const property_figs = await Property.getPropertyFigsByUUID(row.uuid, abortController.signal);
        if (abortController.signal.aborted) return;

        // UPDATED: Fetch health score data when hideInspectionInfo is false (regardless of view mode)
        if (shouldFetchHealthScore && row.uuid) {
          try {
            setLoadingHealthScore(true);
            const propertyHealthScore = await Property.getPropertyHealthScore(row.uuid, abortController.signal);
            if (abortController.signal.aborted) return;
            setHealthScoreData(propertyHealthScore);

            // UPDATED: Also fetch all property health scores for the modal
            const allPropertyHealthScores = await Property.getPropertyAllHealthScore(row.uuid, abortController.signal);
            if (abortController.signal.aborted) return;
            if (allPropertyHealthScores && allPropertyHealthScores.length > 0) {
              setAllPropertyHealthScores(allPropertyHealthScores);
              setHasPropertyHealthScores(true);
            } else {
              setHasPropertyHealthScores(false);
            }
          } catch (error) {
            if (error.name === 'AbortError') return;
            console.error("Error fetching property health score:", error);
            setHealthScoreData(null);
            setHasPropertyHealthScores(false);
          } finally {
            if (!abortController.signal.aborted) {
              setLoadingHealthScore(false);
            }
          }
        }

        if (property_figs && Array.isArray(property_figs.figs) && property_figs.figs.length > 0) {
          const figs = property_figs.figs;

          // Calculate and set the evaluations count with proper UTC handling
          const evaluationsCount = figs.reduce((acc, fig) => {
            const validEvaluations = fig?.evaluations?.filter(evaluation => {
              // Parse UTC timestamp and compare with UTC date range
              const evaluationMoment = moment.utc(evaluation?.created_at);
              return evaluationMoment.isBetween(startMoment, endMoment, null, '[]');
            }) || [];

            // Find the most recent evaluation date
            validEvaluations.forEach(evaluation => {
              const evaluationDate = moment.utc(evaluation.created_at);
              if (!mostRecentEvaluationDate || evaluationDate.isAfter(mostRecentEvaluationDate)) {
                setMostRecentEvaluationDate(evaluationDate);
              }
            });

            return acc + validEvaluations.length;
          }, 0);

          setEvaluationsCount(evaluationsCount);

        } else {
          setEvaluationsCount(0);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error("Error fetching property figs:", error);
      }
    };

    fetchPropertyFigs();
    return () => abortController.abort();
  }, [property, row, startDate, endDate, startMoment, endMoment, shouldFetchHealthScore]);


  // Fixed mostRecentDate calculation to properly handle UTC timestamps
  useEffect(() => {
    const recordedAt = row.vals?.edges[0]?.node?.recorded_at;
    const createdAt = row.checks?.edges[0]?.node?.created_at;
    const datesWithValues = [
      { date: recordedAt, userId: row.lastValUploaded?.wav?.user?.id?.toString() },
      { date: createdAt, userId: row.checks?.edges[0]?.node?.user_id?.toString() }
    ].filter(d => d.date);
    
    if (datesWithValues.length) {
      // Compare UTC timestamps properly
      const mostRecent = datesWithValues.reduce((a, b) => 
        moment.utc(a.date).isAfter(moment.utc(b.date)) ? a : b
      );
      // Convert UTC timestamp to local time for display
      setMostRecentDate(moment.utc(mostRecent.date).local().format("M-D-YY"));
      setLatestUserId(mostRecent.userId); 
    } else {
      setMostRecentDate('N/A');
    }
  }, [property, row, startDate, endDate]);


  useEffect(() => {
    const abortController = new AbortController();
    const resolveUserName = async () => {
      // First try: embedded wav.user.full_name
      if (property?.lastValUploaded?.wav?.user?.full_name) {
        setLatestUserName(property.lastValUploaded.wav.user.full_name);
        return;
      }

      // Second try: embedded user.full_name (top-level)
      if (property?.lastValUploaded?.user?.full_name) {
        setLatestUserName(property.lastValUploaded.user.full_name);
        return;
      }

      // Third try: fetch by user_id
      const userId = property?.lastValUploaded?.wav?.user_id;
      if (userId) {
        try {
          const user = await User.findById(userId, abortController.signal);
          if (abortController.signal.aborted) return;
          if (user && user.full_name) {
            setLatestUserName(user.full_name);
            return;
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Error fetching user:', error);
        }
      }

      // Final fallback
      if (!abortController.signal.aborted) {
        setLatestUserName('N/A');
      }
    };

    resolveUserName();
    return () => abortController.abort();
  }, [property]);

  // Helper: build a compact two-line address string
  const buildAddress = (p) => {
    if (!p) return '';
    const line1 = [p.street1, p.street2].filter(Boolean).join(' ');
    const cityState = [p.city, p.state].filter(Boolean).join(', ');
    const line2 = [cityState, p.zip].filter(Boolean).join(' ');
    return [line1, line2].filter(x => x && x.trim().length > 0).join('\n');
  };

  // Sync user data from parent grid prop (avoids redundant User.findByUUID per card).
  // Falls back to fetching directly if prop is not provided (e.g., PropertyCard used outside grid).
  useEffect(() => {
    const abortController = new AbortController();
    if (parentCurrentUserData) {
      setCurrentUserData(parentCurrentUserData);
    } else if (user && user.uuid) {
      const fetchCurrentUserData = async () => {
        try {
          const freshUserData = await User.findByUUID(user.uuid, abortController.signal);
          if (abortController.signal.aborted) return;
          setCurrentUserData(freshUserData);
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Error fetching current user data:', error);
          setCurrentUserData(user);
        }
      };
      fetchCurrentUserData();
    }
    return () => abortController.abort();
  }, [parentCurrentUserData, user?.uuid, property.uuid]);

  // Check if current property is favorited using fresh user data
  useEffect(() => {
    if (currentUserData && currentUserData.favorite_properties && property.uuid) {
      try {
        const favoriteProperties = JSON.parse(currentUserData.favorite_properties);
        setIsFavorited(favoriteProperties.includes(property.uuid));
      } catch (error) {
        console.error('Error parsing favorite_properties:', error);
        setIsFavorited(false);
      }
    } else {
      setIsFavorited(false);
    }
  }, [currentUserData, property.uuid]);

  const handleFavoriteToggle = async (e) => {
    e.stopPropagation();
    
    if (!user || !property.uuid || updatingFavorite) return;
    
    setUpdatingFavorite(true);
    
    try {
      // CRITICAL: Fetch the most up-to-date user data right before updating
      // This prevents race conditions when multiple cards are favorited quickly
      const freshUserData = await User.findByUUID(user.uuid);
      
      let favoriteProperties = [];
      
      // Parse existing favorites from the fresh data or initialize empty array
      if (freshUserData?.favorite_properties) {
        try {
          favoriteProperties = JSON.parse(freshUserData.favorite_properties);
        } catch (error) {
          console.error('Error parsing existing favorite_properties:', error);
          favoriteProperties = [];
        }
      }
      
      // Determine current state based on fresh data (not component state)
      const isCurrentlyFavorited = favoriteProperties.includes(property.uuid);
      
      // Add or remove property UUID from favorites based on fresh data
      if (isCurrentlyFavorited) {
        // Remove from favorites
        favoriteProperties = favoriteProperties.filter(uuid => uuid !== property.uuid);
      } else {
        // Add to favorites (if not already present)
        if (!favoriteProperties.includes(property.uuid)) {
          favoriteProperties.push(property.uuid);
        }
      }
      
      // Update user record with the fresh data
      await User.update({
        uuid: user.uuid,
        favorite_properties: JSON.stringify(favoriteProperties)
      });
      
      // Update local state based on the action we just performed
      setIsFavorited(!isCurrentlyFavorited);
      
      // Refresh the current user data to ensure it's up to date for next time
      const refreshedUserData = await User.findByUUID(user.uuid);
      setCurrentUserData(refreshedUserData);
      
      // *** NEW: Call the parent callback to update the parent component ***
      if (onFavoritesUpdate && typeof onFavoritesUpdate === 'function') {
        await onFavoritesUpdate();
      }
      
    } catch (error) {
      console.error('Error updating favorite properties:', error);
      toast.error('Failed to update favorite properties');
    } finally {
      setUpdatingFavorite(false);
    }
  };

  const getInspectionsForEquipment = async (equipmentToFetch) => {
    if (Utils.isBlank(property.uuid)) {
      return [];
    }

    let selectedPointNumbers = null;

    const newUploads = await Val.filterBy([
      { field: 'archived', value: 0, comparison: "=" },
      { field: "properties.uuid", value: property.uuid, comparison: "=" },
      { field: 'fig_id', value: equipmentToFetch?.id, comparison: "=" },
      selectedPointNumbers ? { field: "point_num", value: `${_.first(_.first(selectedPointNumbers).split('-'))}`, comparison: "=" } : null,
    ].filter(x => x != null), "recorded_at", "desc", 250, undefined, undefined, undefined, true);

    let uploadList = newUploads.data || [];

    uploadList = uploadList.map(upload => {
      return {
        ...upload,
        sortDay: moment(moment(upload.recorded_at).format("YYYY-MM-DD")).unix(),
        parent_fig_key: upload?.point_info?.parent_fig_key,
        childSort: upload?.point_info?.parent_fig_key === null ? upload?.point_info?.parent_fig_key - 1 : upload?.point_info?.parent_fig_key
      };
    });

    uploadList = _.orderBy(uploadList, [
      upload => moment(moment(upload?.recorded_at).format("YYYY-MM-DD")).unix(),
      upload => upload?.point_info?.parent_fig_key === null ? upload?.point_info?.fig_key + 1 : upload?.point_info?.parent_fig_key,
    ], ['desc', 'desc', 'desc']);

    if (equipmentToFetch !== undefined) {
      uploadList = _.flatten(
        Object.values(
          _.groupBy(uploadList, row => extractPointName(row)
        )
      )
      .map(x => _.first(x)))
      .sort((a, b) => a.point_info && b.point_info && a.point_info.measurement_sort_idx - b.point_info.measurement_sort_idx);
    }

    return uploadList;
  };

  // Add handler for property health score modal
  const handleGetPropertyHealthScore = (event) => {
    event.stopPropagation(); // Prevent the property card onClick from firing
    setShowPropertyHealthModal(true);
  };


  return (
    <Card key={index}
      style={{ maxWidth: '18em', height: 'auto'}}
      className={`align-items-stretch m-2`}
      
    >
      <Card.Body className="p-2">
        <div style={{overflow:'auto'}} className={`d-flex flex-column justify-content-start align-items-center`}>

        
        <div className={`d-flex flex-column justify-content-center align-items-start`}>
          
          <ImageUploading
            multiple={false}
            onChange={async (imageList, addUpdateIndex) => {
              if (imageList.length > 0) {
                setLoading(true);
                await Property.updatePhoto(property.uuid, imageList[0].dataURL, imageList[0].file);
                setCacheBust(new Date().valueOf()); // Update cache bust only after image update
                property.image = {
                  bucket: "dst-photos",
                  key: `${property.uuid}`,
                  region: 'us-west-2',
                }
                property.updated_at = new Date().toISOString();
                setLoading(false);
              }
            }}
            maxNumber={1}
          >
            
           {({ dragProps, isDragging, onImageUpload }) => (
        <div {...dragProps} style={{ width: '100%', position: 'relative', height: '300px', overflow: 'hidden' }}>
            {loading && <ImSpinner3 className="icon-spin" style={{ fontSize: 30, marginTop: '1em', marginBottom: '1em' }} />}
            {!loading && <>
                {property.image
                    ? <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <img 
                            loading="lazy"
                            onClick={(e) => setSelectedPropertyResult(property)} 
                            className="image-thumbnail rounded-lg"
                            style={{ 
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }} 
                            src={`https://s3-${property.image.region}.amazonaws.com/${property.image.bucket}/${property.image.key}?cachebust=${encodeURIComponent(cacheBust || property.updated_at || '')}`}
                        />
                        <div 
                            onClick={e => onImageUpload(e)} 
                            style={{ 
                                position: 'absolute', 
                                top: '0', 
                                left: '0', 
                                color: 'white', 
                                padding: '0.5em', 
                                cursor: 'pointer'
                            }}>
                            <FaEdit style={{ filter: 'drop-shadow(rgb(0,0,0) 0 0 2px)' }} />
                        </div>
                      </div>
                    : <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                          <img 
                            onClick={onImageUpload} 
                            src="/icons/image-placeholder3.svg" 
                            className="image-thumbnail rounded-lg"
                            style={{ 
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                }
            </>}
        </div>
    )}
          
          
          </ImageUploading>
          </div>
   
          {/* Favorite Star Button - moved here */}
          <div className="d-flex justify-content-end w-100 mb-2">
            <div 
              onClick={handleFavoriteToggle} 
              style={{ 
                color: isFavorited ? '#FFD700' : '#666', 
                cursor: updatingFavorite ? 'not-allowed' : 'pointer',
                opacity: updatingFavorite ? 0.5 : 1,
                fontSize: '24px' // Made larger
              }}>
              {updatingFavorite ? (
                <ImSpinner3 className="icon-spin" style={{ fontSize: '20px' }} />
              ) : isFavorited ? (
                <FaStar />
              ) : (
                <FaRegStar />
              )}
            </div>
          </div>
   
          <div className={`d-flex flex-column justify-content-center align-items-center`} onClick={(e) => setSelectedPropertyResult(property)}>
            <h5 className="card-title pt-2 d-flex mb-2">
              <span className="d-inline-block mr-2">
                {hideAnalysisColors ? <></> : property.warningMode 
                  ? <img src="/icons/red-stop-sign.svg" style={{width: "25px", height: "25px"}}/>
                  : <></> // <EvaluationMark criticality={property.evalNum}  />
                }

                {!hideMapLocation && 
                    property.lat !== null && property.lng !== null &&
                      <FaMapMarkerAlt style={{ fontSize: '20px', color: property.lat === mapLat && property.lng === mapLng ? 'blue' : null }} onClick={(e) => {
                        e.stopPropagation();
                        if (property.lat !== null && property.lng != null) {
                          console.log("hovering", property.lat, property.lng)
                          setMapCoordinates(property.lat, property.lng);
                        } else {
                          console.log("no lat/lng", property.lat, property.lng)
                        }
                      }} />
                }
              </span>
              <OverlayTrigger
                placement="top"
                trigger={["hover", "focus"]}
                overlay={
                  <Popover id={`property-address-${property.uuid}`}>
                    {/* Support both react-bootstrap v1 and v2 API names */}
                    {Popover.Title ? (
                      <Popover.Title as="h3" style={{ marginBottom: 0 }}>{property.name}</Popover.Title>
                    ) : (
                      <div className="popover-header">{property.name}</div>
                    )}
                    {Popover.Content ? (
                      <Popover.Content>
                        <div style={{ whiteSpace: 'pre-line' }}>{buildAddress(property)}</div>
                      </Popover.Content>
                    ) : (
                      <div className="popover-body" style={{ whiteSpace: 'pre-line' }}>{buildAddress(property)}</div>
                    )}
                  </Popover>
                }
              >
                <span>{property.name}</span>
              </OverlayTrigger>
            </h5>

            <div className={`d-flex flex-column justify-content-between`}>
            {!hideInspectionInfo && <>
              <span className="d-flex flex-row justify-content-between">
             
              <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">
                  <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '150px' }}>Last Inspection:&nbsp;</span>
                  <span className="small">
                    {mostRecentDate}
                  </span>
                </div>


              </span>
              <span className="d-flex flex-row justify-content-between">
              <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">
                <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '200px' }}>MSi Inspections:&nbsp;</span>
                <span className="small">
                  {/* {property.vals && Object.keys(_.groupBy(property.vals.edges, x => x.node.point_num)).length || 0} */}
                  {uniquePointNumCount || 0}
                  {/* Show loading indicator or use fetched/calculated points total */}
                  {shouldFetchHealthScore ? (
                    loadingHealthScore ? (
                      <> / <ImSpinner3 className="icon-spin" style={{ fontSize: '12px' }} /></>
                    ) : (
                      pointsTotal > 0 ? ` / ${pointsTotal}` : ''
                    )
                  ) : (
                    property.pointInfos ? ` / ${property.pointInfos.totalCount}` : ''
                  )}
                </span>
                </div>
              </span>
              {hideAnalysisColors ? <></> 
                  : <div className="d-flex flex-row">
                    {pointInfos && <>
                      <EvaluationBar
                        criticalityEquivalentColor={25}
                        count={red}
                        total={total} />
                      <EvaluationBar
                        criticalityEquivalentColor={20}
                        count={amber}
                        total={total} />
                      <EvaluationBar
                        criticalityEquivalentColor={15}
                        count={yellow}
                        total={total} />
                      <EvaluationBar
                        criticalityEquivalentColor={10}
                        count={lime}
                        total={total} />
                      <EvaluationBar
                        criticalityEquivalentColor={5}
                        count={green}
                        total={total} />
                      <EvaluationBar
                        criticalityEquivalentColor={null}
                        count={gray}
                        total={total} />
                    </>
                    }
                </div>
                }
              <span className="d-flex flex-row justify-content-between">
              <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '150px' }}>Last Task Upload:&nbsp;</span>
                <span className="small">{property.checks && property.checks.edges.length > 0 && moment(property.checks.edges[0].node.created_at).local().format("M-D-YY") || "N/A"}</span>
              </div>
              </span>
              <span className="d-flex flex-row justify-content-between">
              {!loadingChecks && <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '200px' }}>Tasks Completed:&nbsp;</span>
                <span className="small">
                  {property.checks?.totalCount && property.checks?.totalCount !== 0 ? <MdCheck /> : <MdClear />}
                </span>

                </div>}
                {loadingChecks && <ImSpinner3 className="icon-spin" style={{ fontSize: 20}} />}
              </span>
              </>}




              { !sortByReports && <span className="d-flex flex-row justify-content-between">
              <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

              <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '150px' }}>Last Evaluation Update:&nbsp;</span>
              <span className="small">
              
                     {
                        mostRecentEvaluationDate
                          ? mostRecentEvaluationDate.local().format("M-D-YY")
                          : "N/A"
                      }
              </span>
            </div>
              </span> }




             {!sortByReports && <span className="d-flex flex-row justify-content-between">
              <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '150px' }}>Evaluations:&nbsp;</span>
                {/* <span className="small">{evaluationsCount === 0 ? "No" : "Yes"}</span> */}
                <span className="small">{evaluationsCount}</span>

              </div>
              </span>
              }
            
              

              {!hideInspectionInfo && property.lastValUploaded && 
                <span className="d-flex flex-row justify-content-between">
                                  <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                  {/* <FcBusinesswoman style={{ fontSize: '30px'}} className="mr-2" /> */}
                  <span className="small"  style={{ overflowWrap: 'break-word', maxWidth: '150px' }}>Last Uploader:&nbsp;</span>
                  <span className="small">
                    
                      {/* {property.lastValUploaded && property.lastValUploaded.wav && property.lastValUploaded.wav.user_id} */}
                      {latestUserName}
                  </span>
                  </div>
                </span>
              }

              {!hideReportInfo && property.lastReportRun && <>
              <span >  
                <span className="d-flex flex-row justify-content-between">
                <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                  <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '150px' }}>Last Report Generated:&nbsp;</span>
                  <span className="small">
                    {property.lastReportRun && moment(property.lastReportRun.created_at).local().format("M-D-YY")}
                  </span>
                  </div>
                </span>

                <span className="d-flex flex-row justify-content-between">
                <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                  <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '90px' }}>Report Type:&nbsp;</span>
                  <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '150px' }}>
                    {property.lastReportRun 
                      && processContext(property.lastReportRun.context).itemReportInput
                      && processContext(property.lastReportRun.context).itemReportInput.reportTypeName}
                  </span>
                  </div>
                </span>

                <span className="d-flex flex-row justify-content-between">
                <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                  <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '90px' }}>Created By:&nbsp;</span>
                  <span className="small"  style={{
                  wordWrap: 'break-word',
                  maxWidth: '160px',
                  display: 'inline-block',
                  }}>
  {property.lastReportRun && processContext(property.lastReportRun.context).emailInput?.to}
</span>
                  </div>
                </span>

                <span className="d-flex flex-row justify-content-between">
                <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                  <span className="small">Report Range:&nbsp;</span>
                  <span className="small">
                    {property.lastReportRun && `
                    ${moment(processContext(property.lastReportRun.context).itemReportInput?.startTimestamp).local().format("M-D-YY")}
                    to 
                    ${moment(processContext(property.lastReportRun.context).itemReportInput?.endTimestamp).local().format("M-D-YY")}`}
                  </span>
                  </div>
                </span>
                </span>
                </>   
                }

            {!sortByReports && !hideInspectionInfo && <span className="d-flex flex-row justify-content-between">
                <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                  <span className="small" style={{ overflowWrap: 'break-word', maxWidth: '190px' }}>DST Health Score:&nbsp;</span>
                  <span className="small"  style={{
                  wordWrap: 'break-word',
                  maxWidth: '160px',
                  display: 'inline-block',
                  }}>
                  {/* Show loading indicator or use fetched/calculated health score */}
                  {shouldFetchHealthScore ? (
                    loadingHealthScore ? (
                      <ImSpinner3 className="icon-spin" style={{ fontSize: '12px' }} />
                    ) : (
                      !isNaN(dstHealthScore) && dstHealthScore !== null ? `${dstHealthScore.toFixed(2)}%` : 'N/A'
                    )
                  ) : (
                    !isNaN(dstHealthScore) && `${dstHealthScore.toFixed(2)}%`
                  )}
                  </span>
                  </div>
                </span>}
                

                {property.sheets && property.sheets.length > 0 && <>
                <span className="d-flex flex-row justify-content-between">
                <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">

                  <span className="small">Tasking Sheet Count:&nbsp;</span>
                  <span className="small">
                    {property.sheets.length}
                  </span>
                  </div>
                </span>
                
                </>}

              <span className="d-flex flex-row justify-content-between">
                <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', padding: '0 15px' }} className="d-flex flex-row justify-content-between">
                  <span className="small">Status:&nbsp;</span>
                  <span className="small" style={{ textTransform: 'capitalize' }}>
                    {property.status || 'Not set'}
                  </span>
                </div>
              </span>

            </div>
            
          </div>

        </div>

        {/* DST Health Score Section - MOVED OUTSIDE THE MAIN CLICKABLE AREA */}
        {!sortByReports && !hideInspectionInfo && (
          <div 
            className={`w-100 mt-2 p-2 rounded ${hasPropertyHealthScores ? 'bg-light border border-primary' : 'bg-secondary'}`}
            onClick={hasPropertyHealthScores ? handleGetPropertyHealthScore : () => alert('There are no recorded scores to view currently.')}
            style={{ 
              cursor: hasPropertyHealthScores ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
              boxShadow: hasPropertyHealthScores ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (hasPropertyHealthScores) {
                e.target.style.backgroundColor = '#e3f2fd';
                e.target.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (hasPropertyHealthScores) {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span className="font-weight-bold text-primary" style={{ fontSize: '0.9rem' }}>
                DST Health Score {hasPropertyHealthScores && '📊'}
              </span>
              <span className="badge badge-primary" style={{ fontSize: '0.8rem' }}>
                {shouldFetchHealthScore ? (
                  loadingHealthScore ? (
                    <ImSpinner3 className="icon-spin" style={{ fontSize: '12px' }} />
                  ) : (
                    !isNaN(dstHealthScore) && dstHealthScore !== null ? `${dstHealthScore.toFixed(2)}%` : 'N/A'
                  )
                ) : (
                  !isNaN(dstHealthScore) && `${dstHealthScore.toFixed(2)}%`
                )}
              </span>
            </div>
            {hasPropertyHealthScores && (
              <small className="text-muted d-block mt-1">
                Click to view trend chart
              </small>
            )}
          </div>
        )}

      {
                  hasExternalUuid ? (
                    property.external_uuid ? (
                      <>
                         <FaCircle style={{ marginRight: '5px' }} />
                      </>
                    ) : (
                      <>
                         <FaRegCircle style={{ marginRight: '5px' }} />
                      </>
                    )
                  ) : (
                    <>
                      
                    </>
                  )
                }
  {showMPeButton && (
  <>
    <span 
      style={{ 
        cursor: 'pointer', 
        fontSize: '18px', 
        marginRight: '30px', 
        fontWeight: 'bold',
        // color: isValidMultipoint ? 'green' : 'inherit' // Change color if isValidMultipoint is true
      }} 
      onClick={handlePropertyMPeClick}
    >
      MP<sup>e</sup>
    </span>
  
  </>
)}

{/* Progress Modal */}

   {showProgress && (
        <div style={{ width: '100%', padding: '0.5em', backgroundColor: '#f0f0f0', borderRadius: '5px', marginTop: '0.5em' }}>
          <ProgressBar 
            now={(progress / totalSteps) * 100} 
            label={`${Math.round((progress / totalSteps) * 100)}%`} 
            style={{ height: '1rem', fontSize: '0.75rem' }}
          />
        </div>
      )}

  {/* Property Health Score Modal */}
  <PropertyHealthScoreModal
    show={showPropertyHealthModal}
    onHide={(event) => { 
      if (event) event.stopPropagation(); 
      setShowPropertyHealthModal(false); 
    }}
    healthScores={allPropertyHealthScores}
  />

    </Card.Body>
    </Card>
  )
}


export default PropertyCard;